import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { HiArrowLeft, HiCheck, HiTrash, HiPrinter } from 'react-icons/hi';
import Layout from '../components/Layout';
import Loader from '../components/Loader';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { useNotification } from '../context/NotificationContext';
import CompleteReturnModal from '../components/CompleteReturnModal';
import ConfirmationModal from '../components/ConfirmationModal';
import Tooltip from '../components/Tooltip';
import { getErrorMessage } from '../utils/errorHandler';
import { formatPKR } from '../utils/currencyFormatter';
import { downloadPurchaseReceivingReceiptPdf } from '../utils/purchaseReceivingReceiptPdf';
import { API } from '../config';
import axios from 'axios';
import './Purchases.css';

const getLocalDateString = (d) => {
  const date = d instanceof Date ? d : new Date(d);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getStatusBadge = (status) => {
  if (status === 'completed') return <span className="status-badge active">Completed</span>;
  if (status === 'draft') return <span className="status-badge inactive">Draft</span>;
  return <span className="status-badge inactive">{status || '—'}</span>;
};

const Purchases = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { effectiveBranchId, currentBranch, hasBranches } = useBranch();
  const { success, error } = useNotification();
  const [purchases, setPurchases] = useState([]);
  const [purchaseReturns, setPurchaseReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReturnsList, setShowReturnsList] = useState(false);
  const [highlightedReturnIds, setHighlightedReturnIds] = useState([]);
  const [returnsLoading, setReturnsLoading] = useState(false);
  const [returnStatusFilter, setReturnStatusFilter] = useState('');
  const [showCompleteReturnModal, setShowCompleteReturnModal] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [returnToDelete, setReturnToDelete] = useState(null);
  const [showDeleteReturnConfirm, setShowDeleteReturnConfirm] = useState(false);
  const [deletingReturn, setDeletingReturn] = useState(false);
  const [receiptDownloadingId, setReceiptDownloadingId] = useState(null);
  const [purchaseReturnAccounts, setPurchaseReturnAccounts] = useState([]);
  const [stockTransfersIn, setStockTransfersIn] = useState([]);
  const [transfersLoading, setTransfersLoading] = useState(false);
  const [purchasesTotal, setPurchasesTotal] = useState(0);
  const [transfersTotal, setTransfersTotal] = useState(0);
  const [purchasesPage, setPurchasesPage] = useState(0);
  const [transfersPage, setTransfersPage] = useState(0);
  const PAGE_SIZE = 100;
  const todayLocal = getLocalDateString(new Date());
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  const [filters, setFilters] = useState({
    fromDate: getLocalDateString(oneMonthAgo),
    toDate: todayLocal
  });
  const filtersRef = useRef(filters);
  useEffect(() => { filtersRef.current = filters; }, [filters]);

  const businessId = user?.role === 'super_admin'
    ? (currentBranch?.business_id ?? user?.business_id ?? user?.businessId)
    : (user?.business_id ?? user?.businessId);

  const fetchPurchaseReturns = async () => {
    if (!businessId) return;
    setReturnsLoading(true);
    try {
      const params = new URLSearchParams();
      if (effectiveBranchId) params.set('branchId', effectiveBranchId);
      if (returnStatusFilter) params.set('status', returnStatusFilter);
      const url = `${API}/purchase-returns/business/${businessId}${params.toString() ? `?${params}` : ''}`;
      const res = await axios.get(url);
      setPurchaseReturns(res.data.purchase_returns || []);
    } catch (err) {
      error(getErrorMessage(err));
      setPurchaseReturns([]);
    } finally {
      setReturnsLoading(false);
    }
  };

  const fetchPurchases = async (page = 0) => {
    if (!businessId) {
      setPurchases([]);
      setPurchasesTotal(0);
      setLoading(false);
      return;
    }
    const currentFilters = filtersRef.current || filters;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (effectiveBranchId) params.set('branchId', effectiveBranchId);
      if (currentFilters.fromDate) params.set('fromDate', currentFilters.fromDate);
      if (currentFilters.toDate) params.set('toDate', currentFilters.toDate);
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(page * PAGE_SIZE));
      const res = await axios.get(`${API}/purchases/business/${businessId}?${params}`);
      setPurchases(res.data.purchases || []);
      setPurchasesTotal(res.data.total != null ? res.data.total : (res.data.purchases || []).length);
    } catch (err) {
      setPurchases([]);
      setPurchasesTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchStockTransfersIntoBranch = async (page = 0) => {
    if (!businessId || !effectiveBranchId) {
      setStockTransfersIn([]);
      setTransfersTotal(0);
      return;
    }
    setTransfersLoading(true);
    try {
      const currentFilters = filtersRef.current || filters;
      const params = new URLSearchParams();
      params.set('toBranchId', effectiveBranchId);
      if (currentFilters.fromDate) params.set('fromDate', currentFilters.fromDate);
      if (currentFilters.toDate) params.set('toDate', currentFilters.toDate);
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(page * PAGE_SIZE));
      const res = await axios.get(`${API}/inventory/transfers/business/${businessId}?${params}`);
      setStockTransfersIn(res.data.transfers || []);
      setTransfersTotal(res.data.total != null ? res.data.total : (res.data.transfers || []).length);
    } catch (err) {
      setStockTransfersIn([]);
      setTransfersTotal(0);
    } finally {
      setTransfersLoading(false);
    }
  };

  useEffect(() => {
    setPurchasesPage(0);
    setTransfersPage(0);
    fetchPurchases(0);
    if (effectiveBranchId && businessId) fetchStockTransfersIntoBranch(0);
  }, [businessId, effectiveBranchId]);

  useEffect(() => {
    if (showReturnsList && businessId) fetchPurchaseReturns();
  }, [showReturnsList, businessId, effectiveBranchId, returnStatusFilter]);

  useEffect(() => {
    const state = location.state;
    if (state?.viewReturns === true) {
      setShowReturnsList(true);
      if (Array.isArray(state.highlightReturnIds) && state.highlightReturnIds.length > 0) {
        setHighlightedReturnIds(state.highlightReturnIds);
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []);

  useEffect(() => {
    if (highlightedReturnIds.length > 0) {
      const t = setTimeout(() => setHighlightedReturnIds([]), 8000);
      return () => clearTimeout(t);
    }
  }, [highlightedReturnIds.length]);

  useEffect(() => {
    if (!showCompleteReturnModal || !selectedReturn || !businessId) return;
    const branchId = selectedReturn.branch_id ?? selectedReturn.branchId ?? effectiveBranchId;
    const params = branchId ? `?branchId=${branchId}` : '';
    let cancelled = false;
    axios.get(`${API}/funds/business/${businessId}/accounts${params}`)
      .then((res) => { if (!cancelled) setPurchaseReturnAccounts(res.data.accounts || []); })
      .catch(() => { if (!cancelled) setPurchaseReturnAccounts([]); });
    return () => { cancelled = true; };
  }, [showCompleteReturnModal, selectedReturn?.id, selectedReturn?.branch_id, selectedReturn?.branchId, businessId, effectiveBranchId]);

  const handleCompleteReturn = (purchaseReturn) => {
    setSelectedReturn(purchaseReturn);
    setShowCompleteReturnModal(true);
  };

  const handleDeleteReturnClick = (ret) => {
    setReturnToDelete(ret);
    setShowDeleteReturnConfirm(true);
  };

  const confirmDeleteReturn = async () => {
    if (!returnToDelete || deletingReturn) return;
    setDeletingReturn(true);
    try {
      await axios.delete(`${API}/purchase-returns/${returnToDelete.id}`);
      success('Purchase return deleted.');
      setShowDeleteReturnConfirm(false);
      setReturnToDelete(null);
      fetchPurchaseReturns();
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setDeletingReturn(false);
    }
  };

  const handleCompleteReturnSubmit = async (data) => {
    if (!selectedReturn) return;
    const branchId = selectedReturn.purchase?.branch_id ?? selectedReturn.purchase?.branchId ?? selectedReturn.branch_id ?? selectedReturn.branchId ?? effectiveBranchId;
    const payload = { ...data, ...(branchId != null && branchId !== '' ? { branchId: parseInt(branchId, 10) } : {}) };
    try {
      await axios.post(`${API}/purchase-returns/${selectedReturn.id}/complete`, payload);
      success('Purchase return completed. Refund recorded and inventory updated.');
      setShowCompleteReturnModal(false);
      setSelectedReturn(null);
      fetchPurchaseReturns();
      fetchPurchases();
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const handleDownloadReceivingReceipt = async (purchase) => {
    const status = String(purchase?.status || '').toLowerCase();
    if (status !== 'confirmed' && status !== 'completed') {
      error('Receiving receipt is only available for confirmed purchases.');
      return;
    }
    if (receiptDownloadingId != null) return;
    setReceiptDownloadingId(purchase.id);
    try {
      let businessName = 'Business';
      if (businessId) {
        const bizRes = await axios.get(`${API}/businesses/${businessId}`);
        const biz = bizRes.data?.business || bizRes.data;
        businessName = biz?.name || businessName;
      }
      await downloadPurchaseReceivingReceiptPdf({
        businessName,
        branchName: purchase?.branch?.name || currentBranch?.name || '—',
        purchase
      });
      success('Receiving receipt downloaded.');
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setReceiptDownloadingId(null);
    }
  };

  const purchasesStart = purchasesPage * PAGE_SIZE + 1;
  const purchasesEnd = Math.min((purchasesPage + 1) * PAGE_SIZE, purchasesTotal);
  const transfersStart = transfersPage * PAGE_SIZE + 1;
  const transfersEnd = Math.min((transfersPage + 1) * PAGE_SIZE, transfersTotal);
  const purchasesTotalAmount = purchases.reduce((sum, p) => sum + (parseFloat(p.total_amount ?? p.totalAmount) || 0), 0);

  return (
    <Layout>
      <div className="purchases-page">
        <div className="page-header">
          <div>
            <h1>Purchases</h1>
            <p className="page-subtitle">
              {currentBranch ? <>Branch: <strong>{currentBranch.name}</strong> · </> : null}
              View purchases and stock transferred in. Filter by date range.
            </p>
          </div>
          <button
            type="button"
            className="create-button purchases-view-returns-btn"
            onClick={() => setShowReturnsList(!showReturnsList)}
            style={{ background: showReturnsList ? 'var(--primary-color, #6d28d9)' : 'var(--border-color, #f3f4f6)', color: showReturnsList ? 'white' : 'var(--text-color, #374151)' }}
          >
            <HiArrowLeft /> {showReturnsList ? 'View Purchases' : 'View Returns'}
          </button>
        </div>

        {!hasBranches && (
          <div className="banner no-branches-banner">
            Create at least one branch to see purchases. Go to <a href="/branches">Branches</a>.
          </div>
        )}

        {hasBranches && (
          <>
            {showReturnsList ? (
              <div className="purchases-returns-section">
                <div className="returns-list-header">
                  <h2>Purchase Returns</h2>
                  <div className="returns-list-filters">
                    <label htmlFor="purchase-return-status-filter">Status:</label>
                    <select
                      id="purchase-return-status-filter"
                      value={returnStatusFilter}
                      onChange={(e) => setReturnStatusFilter(e.target.value)}
                      className="filter-dropdown"
                    >
                      <option value="">All</option>
                      <option value="draft">Draft</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                </div>
                <p className="returns-helper-text">
                  Complete draft returns to process refund and update inventory &amp; vendor ledger.
                </p>
                {returnsLoading ? (
                  <Loader message="Loading purchase returns…" variant="block" />
                ) : purchaseReturns.length === 0 ? (
                  <div className="empty-state">
                    <p>No purchase returns found.</p>
                  </div>
                ) : (
                  <div className="purchases-table-wrap">
                    <table className="purchases-table data-table">
                      <thead>
                        <tr>
                          <th>Return #</th>
                          <th>Purchase #</th>
                          <th>Vendor</th>
                          <th>Date</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {purchaseReturns.map((ret) => (
                          <tr
                            key={ret.id}
                            className={highlightedReturnIds.includes(ret.id) ? 'purchase-return-row-highlighted' : ''}
                          >
                            <td className="font-mono">{ret.return_number || `PR-${ret.id}`}</td>
                            <td>{ret.purchase?.purchase_number || ret.purchase_id || '—'}</td>
                            <td>{ret.vendor?.name || '—'}</td>
                            <td>{ret.return_date ? new Date(ret.return_date).toLocaleDateString() : '—'}</td>
                            <td className="font-semibold">{formatPKR(ret.total_amount ?? 0)}</td>
                            <td>{getStatusBadge(ret.status)}</td>
                            <td>
                              <div className="action-buttons">
                                {ret.status === 'draft' && (
                                  <>
                                    <Tooltip text="Complete return" position="top">
                                      <button
                                        type="button"
                                        className="action-btn confirm-btn"
                                        onClick={() => handleCompleteReturn(ret)}
                                      >
                                        <HiCheck />
                                      </button>
                                    </Tooltip>
                                    <Tooltip text="Delete return" position="top">
                                      <button
                                        type="button"
                                        className="action-btn delete-btn"
                                        onClick={() => handleDeleteReturnClick(ret)}
                                      >
                                        <HiTrash />
                                      </button>
                                    </Tooltip>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="purchases-filters">
                  <div className="filter-group">
                    <label>From date</label>
                    <input
                      type="date"
                      value={filters.fromDate}
                      onChange={e => setFilters(f => ({ ...f, fromDate: e.target.value }))}
                    />
                  </div>
                  <div className="filter-group">
                    <label>To date</label>
                    <input
                      type="date"
                      value={filters.toDate}
                      onChange={e => setFilters(f => ({ ...f, toDate: e.target.value }))}
                    />
                  </div>
                  <button type="button" className="btn-primary" onClick={() => { setPurchasesPage(0); setTransfersPage(0); fetchPurchases(0); fetchStockTransfersIntoBranch(0); }} disabled={loading}>
                    {loading ? 'Loading…' : 'Apply'}
                  </button>
                </div>

                {loading ? (
                  <Loader message="Loading purchases…" variant="block" />
                ) : (
                  <>
                    <div className="purchases-card">
                      <div className="purchases-card-header">
                        <h2 className="purchases-card-title">Purchases</h2>
                        {purchasesTotal > 0 && (
                          <div className="purchases-summary">
                            <span className="purchases-summary-count">{purchasesTotal} record{purchasesTotal !== 1 ? 's' : ''}</span>
                            {purchases.length > 0 && (
                              <span className="purchases-summary-amount">Page total: {formatPKR(purchasesTotalAmount)}</span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="purchases-table-wrap">
                        {purchases.length === 0 ? (
                          <div className="purchases-empty-state">
                            <p className="purchases-empty">No purchases in this date range.</p>
                            <p className="purchases-empty-hint">Adjust the date range above and click Apply.</p>
                          </div>
                        ) : (
                          <>
                            <table className="purchases-table data-table">
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th>Product code(s)</th>
                                  <th>Product name(s)</th>
                                  <th>Vendor</th>
                                  <th>Branch</th>
                                  <th>Status</th>
                                  <th className="col-num">Total</th>
                                  <th>Receipt</th>
                                </tr>
                              </thead>
                              <tbody>
                                {purchases.map(p => {
                                  const codes = (p.items || [])
                                    .map(i => i.product?.product_code ?? i.product?.productCode)
                                    .filter(Boolean);
                                  const names = (p.items || [])
                                    .map(i => i.product?.name ?? i.product?.productName)
                                    .filter(Boolean);
                                  const productCodes = [...new Set(codes)].join(', ') || '—';
                                  const productNames = [...new Set(names)].join(', ') || '—';
                                  return (
                                    <tr key={p.id}>
                                      <td>{p.purchase_date || p.purchaseDate || '—'}</td>
                                      <td>{productCodes}</td>
                                      <td>{productNames}</td>
                                      <td>{p.party?.name || '—'}</td>
                                      <td>{p.branch?.name || '—'}</td>
                                      <td>
                                        <span className={`status-badge ${p.status === 'confirmed' ? 'active' : 'inactive'}`}>
                                          {p.status || '—'}
                                        </span>
                                      </td>
                                      <td className="col-num">{formatPKR(p.total_amount ?? p.totalAmount ?? 0)}</td>
                                      <td>
                                        <button
                                          type="button"
                                          className="btn-secondary btn-sm"
                                          onClick={() => handleDownloadReceivingReceipt(p)}
                                          disabled={receiptDownloadingId === p.id || (p.status !== 'confirmed' && p.status !== 'completed')}
                                          title={(p.status !== 'confirmed' && p.status !== 'completed') ? 'Confirm purchase to generate receipt' : 'Download receiving receipt'}
                                        >
                                          <HiPrinter /> {receiptDownloadingId === p.id ? 'Preparing…' : 'Receipt'}
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                            {purchasesTotal > PAGE_SIZE && (
                              <div className="purchases-pagination">
                                <span className="purchases-pagination-info">
                                  Showing {purchasesStart}–{purchasesEnd} of {purchasesTotal}
                                </span>
                                <div className="purchases-pagination-btns">
                                  <button
                                    type="button"
                                    className="btn-secondary btn-sm"
                                    disabled={purchasesPage <= 0 || loading}
                                    onClick={() => { const prev = purchasesPage - 1; setPurchasesPage(prev); fetchPurchases(prev); }}
                                  >
                                    Previous
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-secondary btn-sm"
                                    disabled={purchasesEnd >= purchasesTotal || loading}
                                    onClick={() => { const next = purchasesPage + 1; setPurchasesPage(next); fetchPurchases(next); }}
                                  >
                                    Next
                                  </button>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {effectiveBranchId && (
                      <div className="purchases-transfers-section">
                        <div className="purchases-card">
                          <div className="purchases-card-header">
                            <h2 className="purchases-card-title">Stock transferred into this branch</h2>
                            {transfersTotal > 0 && (
                              <span className="purchases-summary-count">{transfersTotal} line item{transfersTotal !== 1 ? 's' : ''}</span>
                            )}
                          </div>
                          {transfersLoading ? (
                            <Loader message="Loading transfers…" variant="block" />
                          ) : stockTransfersIn.length === 0 ? (
                            <div className="purchases-empty-state">
                              <p className="purchases-empty">No stock transfers into this branch in the selected date range.</p>
                              <p className="purchases-empty-hint">Transfers from the main branch will appear here.</p>
                            </div>
                          ) : (
                            <>
                              <div className="purchases-table-wrap">
                                <table className="purchases-table data-table">
                                  <thead>
                                    <tr>
                                      <th>Date</th>
                                      <th>Transfer #</th>
                                      <th>From branch</th>
                                      <th>Product</th>
                                      <th>Variant</th>
                                      <th className="col-num">Qty</th>
                                      <th className="col-num">Unit price</th>
                                      <th className="col-num">Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {stockTransfersIn.map(t => {
                                      const qty = parseFloat(t.quantity) || 0;
                                      const up = parseFloat(t.unit_price) || 0;
                                      const total = qty * up;
                                      const variantLabel = [t.variant?.color, t.variant?.size].filter(Boolean).join(' / ') || '—';
                                      return (
                                        <tr key={t.id}>
                                          <td>{t.transfer_date || t.transferDate || '—'}</td>
                                          <td className="font-mono">{t.transfer_number || t.transferNumber || `T-${t.id}`}</td>
                                          <td>{t.from_branch?.name || t.fromBranch?.name || '—'}</td>
                                          <td>{t.product?.name || t.product?.productCode || '—'}</td>
                                          <td>{variantLabel}</td>
                                          <td className="col-num">{qty}</td>
                                          <td className="col-num">{formatPKR(up)}</td>
                                          <td className="col-num">{formatPKR(total)}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                              {transfersTotal > PAGE_SIZE && (
                                <div className="purchases-pagination">
                                  <span className="purchases-pagination-info">
                                    Showing {transfersStart}–{transfersEnd} of {transfersTotal}
                                  </span>
                                  <div className="purchases-pagination-btns">
                                    <button
                                      type="button"
                                      className="btn-secondary btn-sm"
                                      disabled={transfersPage <= 0 || transfersLoading}
                                      onClick={() => { const prev = transfersPage - 1; setTransfersPage(prev); fetchStockTransfersIntoBranch(prev); }}
                                    >
                                      Previous
                                    </button>
                                    <button
                                      type="button"
                                      className="btn-secondary btn-sm"
                                      disabled={transfersEnd >= transfersTotal || transfersLoading}
                                      onClick={() => { const next = transfersPage + 1; setTransfersPage(next); fetchStockTransfersIntoBranch(next); }}
                                    >
                                      Next
                                    </button>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}

        <CompleteReturnModal
          isOpen={showCompleteReturnModal}
          onClose={() => {
            setShowCompleteReturnModal(false);
            setSelectedReturn(null);
          }}
          onConfirm={handleCompleteReturnSubmit}
          returnData={selectedReturn}
          returnType="purchase"
          accounts={purchaseReturnAccounts}
        />

        <ConfirmationModal
          isOpen={showDeleteReturnConfirm}
          onClose={() => { setShowDeleteReturnConfirm(false); setReturnToDelete(null); }}
          onConfirm={confirmDeleteReturn}
          title="Delete purchase return?"
          message={returnToDelete ? `Are you sure you want to delete return ${returnToDelete.return_number || `#${returnToDelete.id}`}? This cannot be undone.` : ''}
          confirmText={deletingReturn ? 'Deleting…' : 'Delete'}
          cancelText="Cancel"
          type="danger"
        />
      </div>
    </Layout>
  );
};

export default Purchases;
